import { t } from "i18next";
import { MagicStar } from "iconsax-react";
import { useEffect, useMemo, useRef, useState } from "react";
import DBButton from "../../../../../component/global/DBButton";
import DBFieldBlock from "../../../../../component/global/DBFieldBlock";
import DBInput from "../../../../../component/global/DBInput";
import DBSelect from "../../../../../component/global/DBSelect";
import DBSwitch from "../../../../../component/global/DBSwitch";
import DBTextArea from "../../../../../component/global/DBTextArea";
import { AiCoinHint } from "../../../../../features/ai-billing/AiCoinGate";
import {
    PAPER_SIZES,
    POSTER_FONTS,
    POSTER_FOCUS_ELEMENTS,
    POSTER_ICONS,
    SAMPLE_ITEM_NAME,
    FALLBACK_TEMPLATES,
    ensurePosterFontsLoaded,
} from "./constants";
import AIPrintPosterModal from "./AIPrintPosterModal";
import PrintPosterPreview from "./PrintPosterPreview";

function ColorField({ id, label, value, onChange }) {
    return (
        <div className="print-poster-color">
            <label htmlFor={id}>{label}</label>
            <div className="print-poster-color__control">
                <input
                    id={id}
                    type="color"
                    value={
                        /^#[0-9A-Fa-f]{6}$/.test(value || "")
                            ? value
                            : "#000000"
                    }
                    onChange={(e) => onChange(e.target.value.toUpperCase())}
                    aria-label={label}
                />
                <DBInput
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    maxLength={7}
                />
            </div>
        </div>
    );
}

export default function PrintPosterDesigner({
    poster,
    templates,
    company,
    onChange,
}) {
    const [aiOpen, setAiOpen] = useState(false);
    const [savedAiPoster, setSavedAiPoster] = useState(null);
    const [useAiDesign, setUseAiDesign] = useState(false);
    const [preAiPoster, setPreAiPoster] = useState(null);
    const previewRef = useRef(null);
    const paperOptions = Object.keys(PAPER_SIZES).map((id) => ({
        value: id,
        label: t(`print.poster.paper.${id}`),
    }));
    const iconOptions = POSTER_ICONS.map((icon) => ({
        value: icon,
        label: t(`print.poster.icon.${icon}`),
    }));
    const templateList = templates?.length ? templates : FALLBACK_TEMPLATES;
    const selectedTemplate = useMemo(
        () =>
            templateList.find((item) => item.id === poster?.template_id) ||
            templateList[0],
        [templateList, poster?.template_id]
    );

    useEffect(() => {
        ensurePosterFontsLoaded();
    }, []);

    if (!poster) return null;

    const update = (patch) => onChange({ ...poster, ...patch });
    const updateColor = (key, value) =>
        update({ colors: { ...poster.colors, [key]: value } });
    const updateValue = (index, patch) => {
        const values = (poster.values || []).map((item, i) =>
            i === index ? { ...item, ...patch } : item
        );
        update({ values });
    };

    const applyTemplate = (template) => {
        update({
            template_id: template.id,
            paper_size: template.paper_size,
            orientation: template.orientation,
            colors: { ...template.colors },
        });
    };
    const applyAiPoster = (generatedPoster, sourcePoster = poster) => {
        onChange({
            ...sourcePoster,
            ...generatedPoster,
            brand_name: sourcePoster.brand_name,
            show_logo: sourcePoster.show_logo,
        });
    };
    const handleAiSaved = (generatedPoster) => {
        const nextPoster = { ...generatedPoster, ai_generated: true };
        setSavedAiPoster(nextPoster);
        if (useAiDesign) {
            applyAiPoster(nextPoster, preAiPoster || poster);
        }
    };
    const toggleUseAiDesign = (checked) => {
        if (checked) {
            if (!savedAiPoster) return;
            const snapshot = poster;
            setPreAiPoster(snapshot);
            applyAiPoster(savedAiPoster, snapshot);
            setUseAiDesign(true);
            return;
        }
        if (preAiPoster) {
            onChange(preAiPoster);
        }
        setUseAiDesign(false);
    };

    return (
        <section className="print-poster-designer" aria-labelledby="print-poster-heading">
            <div className="print-poster-designer__intro">
                <div>
                    <h2 id="print-poster-heading">{t("print.poster.title")}</h2>
                    <p>{t("print.poster.description")}</p>
                </div>
            </div>
            <div className="print-poster-designer__ai">
                <span className="print-poster-designer__ai-icon" aria-hidden="true">
                    <MagicStar size={22} />
                </span>
                <div className="print-poster-designer__ai-copy">
                    <strong>{t("print.poster.ai.button")}</strong>
                    <span>{t("print.poster.ai.helper")}</span>
                </div>
                <div className="print-poster-designer__ai-actions">
                    <DBButton
                        className="print-poster-designer__ai-button"
                        aria-haspopup="dialog"
                        caption={
                            <>
                                {t("print.poster.ai.start")}
                                <AiCoinHint operation="print_poster" />
                            </>
                        }
                        onClick={() => setAiOpen(true)}
                    />
                    {savedAiPoster ? (
                        <label className="print-poster-designer__ai-use">
                            <span>{t("print.poster.ai.use")}</span>
                            <DBSwitch
                                checked={useAiDesign}
                                onChange={toggleUseAiDesign}
                            />
                        </label>
                    ) : null}
                </div>
            </div>

            <div className="print-poster-templates">
                {templateList.map((template) => {
                    const selected = template.id === poster?.template_id;
                    return (
                        <button
                            type="button"
                            key={template.id}
                            className={`print-poster-template${selected ? " is-selected" : ""}`}
                            aria-pressed={selected}
                            onClick={() => applyTemplate(template)}
                            style={{
                                "--swatch-bg": template.colors.background,
                                "--swatch-text": template.colors.text,
                                "--swatch-accent": template.colors.accent,
                            }}
                        >
                            <span className="print-poster-template__swatch" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                            </span>
                            <strong>{t(`print.poster.template.${template.id}`)}</strong>
                            <small>{t(`print.poster.template.${template.id}.hint`)}</small>
                        </button>
                    );
                })}
            </div>

            <div className="print-poster-designer__layout">
                <div className="print-poster-designer__form">
                    <div className="print-poster-designer__grid">
                        <DBFieldBlock title={t("print.poster.paper")}>
                            <DBSelect
                                value={poster.paper_size}
                                options={paperOptions}
                                onChange={(value) =>
                                    update({
                                        paper_size: value,
                                        orientation:
                                            value === "tent"
                                                ? "landscape"
                                                : selectedTemplate?.orientation ||
                                                  "portrait",
                                    })
                                }
                                style={{ width: "100%" }}
                            />
                        </DBFieldBlock>
                        <DBFieldBlock title={t("print.poster.focus")}>
                            <DBSelect
                                value={poster.focus_element || "qr"}
                                options={POSTER_FOCUS_ELEMENTS.map((id) => ({
                                    value: id,
                                    label: t(`print.poster.focus.${id}`),
                                }))}
                                onChange={(value) =>
                                    update({ focus_element: value })
                                }
                                style={{ width: "100%" }}
                            />
                        </DBFieldBlock>
                    </div>
                    <DBFieldBlock title={t("print.poster.font")}>
                        <div className="print-poster-fonts">
                            {POSTER_FONTS.map((font) => {
                                const selected =
                                    (poster.font_family || "yekan") === font.id;
                                return (
                                    <button
                                        type="button"
                                        key={font.id}
                                        className={`print-poster-font${selected ? " is-selected" : ""}`}
                                        aria-pressed={selected}
                                        style={{ fontFamily: font.stack }}
                                        onClick={() =>
                                            update({ font_family: font.id })
                                        }
                                    >
                                        {t(`print.poster.font.${font.id}`)}
                                    </button>
                                );
                            })}
                        </div>
                    </DBFieldBlock>
                    <div className="print-poster-designer__grid">
                        <DBFieldBlock title={t("print.poster.brand.name")}>
                            <DBInput
                                value={poster.brand_name}
                                onChange={(e) =>
                                    update({ brand_name: e.target.value })
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock title={t("print.poster.brand.subtitle")}>
                            <DBInput
                                value={poster.brand_subtitle}
                                onChange={(e) =>
                                    update({ brand_subtitle: e.target.value })
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock title={t("print.poster.affiliation")}>
                            <DBInput
                                value={poster.affiliation}
                                onChange={(e) =>
                                    update({ affiliation: e.target.value })
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock title={t("print.poster.headline")}>
                            <DBInput
                                value={poster.headline}
                                onChange={(e) =>
                                    update({ headline: e.target.value })
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock title={t("print.poster.subtitle")}>
                            <DBInput
                                value={poster.subtitle}
                                onChange={(e) =>
                                    update({ subtitle: e.target.value })
                                }
                            />
                        </DBFieldBlock>
                    </div>
                    <DBFieldBlock title={t("print.poster.instruction")}>
                        <DBTextArea
                            rows={3}
                            value={poster.instruction}
                            onChange={(e) =>
                                update({ instruction: e.target.value })
                            }
                        />
                    </DBFieldBlock>
                    <div className="print-poster-values-edit">
                        <p>{t("print.poster.values")}</p>
                        {(poster.values || []).map((value, index) => (
                            <div className="print-poster-value-row" key={index}>
                                <DBSelect
                                    value={value.icon}
                                    options={iconOptions}
                                    onChange={(icon) =>
                                        updateValue(index, { icon })
                                    }
                                    style={{ width: "100%" }}
                                    aria-label={t("print.poster.value.icon")}
                                />
                                <DBInput
                                    value={value.text}
                                    onChange={(e) =>
                                        updateValue(index, {
                                            text: e.target.value,
                                        })
                                    }
                                    aria-label={t("print.poster.value.text")}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="print-poster-designer__grid">
                        <DBFieldBlock title={t("print.poster.footer")}>
                            <DBInput
                                value={poster.footer}
                                onChange={(e) =>
                                    update({ footer: e.target.value })
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock title={t("print.poster.signoff")}>
                            <DBInput
                                value={poster.signoff}
                                onChange={(e) =>
                                    update({ signoff: e.target.value })
                                }
                            />
                        </DBFieldBlock>
                        <DBFieldBlock title={t("print.poster.badge.label")}>
                            <DBInput
                                value={poster.badge_label}
                                onChange={(e) =>
                                    update({ badge_label: e.target.value })
                                }
                            />
                        </DBFieldBlock>
                    </div>
                    <div className="print-poster-toggles">
                        <label>
                            <span>{t("print.poster.show.badge")}</span>
                            <DBSwitch
                                checked={poster.show_badge}
                                onChange={(checked) =>
                                    update({ show_badge: checked })
                                }
                            />
                        </label>
                        <label>
                            <span>{t("print.poster.show.logo")}</span>
                            <DBSwitch
                                checked={poster.show_logo}
                                onChange={(checked) =>
                                    update({ show_logo: checked })
                                }
                            />
                        </label>
                    </div>
                    <div className="print-poster-colors">
                        <ColorField
                            id="poster-color-bg"
                            label={t("print.poster.color.background")}
                            value={poster.colors?.background}
                            onChange={(value) => updateColor("background", value)}
                        />
                        <ColorField
                            id="poster-color-text"
                            label={t("print.poster.color.text")}
                            value={poster.colors?.text}
                            onChange={(value) => updateColor("text", value)}
                        />
                        <ColorField
                            id="poster-color-accent"
                            label={t("print.poster.color.accent")}
                            value={poster.colors?.accent}
                            onChange={(value) => updateColor("accent", value)}
                        />
                        <ColorField
                            id="poster-color-muted"
                            label={t("print.poster.color.muted")}
                            value={poster.colors?.muted}
                            onChange={(value) => updateColor("muted", value)}
                        />
                    </div>
                </div>
                <div className="print-poster-designer__preview">
                    <p>{t("print.poster.preview")}</p>
                    <div className="print-poster-preview-frame">
                        <PrintPosterPreview
                            poster={poster}
                            company={company}
                            itemName={SAMPLE_ITEM_NAME}
                            previewRef={previewRef}
                        />
                    </div>
                </div>
            </div>

            <AIPrintPosterModal
                show={aiOpen}
                onHide={() => setAiOpen(false)}
                paperSize={poster.paper_size}
                focusElement={poster.focus_element}
                company={company}
                existingPoster={
                    savedAiPoster || (poster.ai_generated ? poster : null)
                }
                onGenerated={handleAiSaved}
            />
        </section>
    );
}
